import { useState } from 'react';

import type { PendingApproval } from '@/api/inspections';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Field, textAreaClass } from '@/components/Field';
import { Button, Notice, SectionTitle } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDateTime } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import { decisionProblems, uncovered, waitedFor } from './queue';

/**
 * One charge, and the two things a manager can do about it.
 *
 * <p>
 * <strong>Both decisions live in one panel, opened by reading rather than by deciding.</strong> A
 * row with Approve and Refuse buttons on it invites the decision to be made from the total alone,
 * which is the failure the whole control exists to prevent — the threshold stopped this charge so
 * that somebody would look at what it is made of. So the row opens the breakdown, and the buttons
 * are underneath it.
 * </p>
 *
 * <p>
 * Presentational, like the two booking counters: it takes a row and an intent, and the page owns
 * the mutation.
 * </p>
 */
export function ApprovalDrawer({
  row,
  saving,
  onDecide,
  onClose,
}: {
  row: PendingApproval;
  saving: boolean;
  onDecide: (payload: { approve: boolean; note: string | null }) => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState('');

  const focusRef = useAutoFocusFirstField<HTMLDivElement>();

  const gap = uncovered(row);
  const refusalProblems = decisionProblems(false, note);

  return (
    <div className="fixed inset-0 z-9999 flex justify-end" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 cursor-default bg-[var(--overlay-scrim)]"
        onClick={onClose}
      />

      <div
        ref={focusRef}
        {...enterKeyScope}
        className="relative flex h-full w-full max-w-[560px] flex-col border-l border-[var(--overlay-border)] bg-[var(--overlay-surface)] shadow-lg"
      >
        <div className="border-b border-border-color p-4">
          <h2 className="text-base font-semibold text-gray-900">{row.garmentName}</h2>
          <p className="mt-0.5 text-[13px] text-default">
            <span className="font-mono-nums">{row.serialCode}</span>
            {row.bookingNumber && (
              <>
                {' · '}
                <span className="font-mono-nums">{row.bookingNumber}</span>
              </>
            )}
            {` · ${row.customerName}`}
          </p>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          <Notice tone="warning" title="Nothing has been billed for this yet">
            The findings are on the record and the money is not. Until this is signed off, the
            deposit settles as though the garment came back clean — so the customer is waiting on
            this, not on the shop.
          </Notice>

          <div>
            <SectionTitle>What is being claimed</SectionTitle>

            {row.replacementValueUsed ? (
              <p className="mt-3 text-[13px] text-default">
                The garment is written off. What the customer is being asked for is the
                design&rsquo;s replacement value — not the lines below, which are the evidence for
                the write-off rather than the charge.
              </p>
            ) : row.findings.length === 0 ? (
              <p className="mt-3 text-[13px] text-default">
                Nothing was ticked on the checklist. Worth asking about before signing.
              </p>
            ) : null}

            {row.findings.length > 0 && (
              <ul className="mt-3 list-none space-y-1.5 ps-0">
                {row.findings.map((finding) => (
                  <li key={finding.id} className="flex justify-between gap-3 text-[13px]">
                    <span className="min-w-0">
                      <span className="text-gray-900">{finding.damageTypeName}</span>
                      {finding.note && (
                        <span className="mt-0.5 block text-default">{finding.note}</span>
                      )}
                    </span>
                    <span className="font-mono-nums whitespace-nowrap text-gray-900">
                      {formatAmount(finding.charge)}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-3 flex justify-between gap-3 border-t border-border-color pt-3">
              <span className="text-sm font-medium text-dark">
                {row.replacementValueUsed ? 'Replacement value' : 'Damage charge'}
              </span>
              <span className="font-mono-nums text-sm font-semibold text-gray-900">
                {formatAmount(row.proposedCharge)}
              </span>
            </div>

            <div className="mt-1 flex justify-between gap-3 text-[13px]">
              <span className="text-default">Deposit held</span>
              <span className="font-mono-nums text-default">
                {row.depositHeld === null ? 'none taken' : formatAmount(row.depositHeld)}
              </span>
            </div>
          </div>

          {/* The half a total on its own hides: approving above the deposit is not a deduction,
              it is an invoice somebody has to go and collect. */}
          {gap !== null && (
            <Notice tone="danger" title={`${formatAmount(gap)} more than the deposit covers`}>
              Approving this deducts what the shop is holding and leaves the rest to collect from
              the customer. Nothing here chases it.
            </Notice>
          )}

          {!row.acknowledgedByCustomer && (
            <Notice tone="warning" title="The customer did not agree to this at the counter">
              Whoever inspected it left that unticked, which is what it is there for. This is the
              kind that comes back as a dispute.
            </Notice>
          )}

          {row.notes && (
            <div>
              <SectionTitle>What the counter wrote</SectionTitle>
              <p className="mt-2 text-[13px] whitespace-pre-wrap text-default">{row.notes}</p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 text-[13px] text-default">
            <StatusBadge tone="neutral">{waitedFor(row.daysWaiting)}</StatusBadge>
            <span>
              Inspected {formatDateTime(row.inspectedOn)}
              {row.inspectedByName ? ` by ${row.inspectedByName}` : ''}
            </span>
          </div>

          <Field
            label="Note"
            hint="Required to refuse. Whoever quoted the figure has to tell the customer something, and “rejected” is not that."
          >
            <textarea
              className={textAreaClass}
              rows={3}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </Field>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-border-color p-4">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            icon="icon icon-x-circle"
            disabled={saving || refusalProblems.length > 0}
            title={refusalProblems[0]}
            onClick={() => onDecide({ approve: false, note: note.trim() })}
          >
            Refuse
          </Button>
          <Button
            variant="primary"
            icon="icon icon-badge-check"
            shortcut="save"
            data-enter-key-commit
            loading={saving}
            disabled={saving}
            onClick={() =>
              onDecide({ approve: true, note: note.trim() === '' ? null : note.trim() })
            }
          >
            Approve {formatAmount(row.proposedCharge)}
          </Button>
        </div>
      </div>
    </div>
  );
}
